<template>
  <div class="produtos-container">

    <!-- ================= HEADER ================= -->
    <div class="page-header">
      <div class="d-flex flex-column">
        <div class="d-flex align-center flex-wrap">
          <div class="d-flex align-center">
            <v-avatar size="48" color="blue-darken-1" class="mr-3" variant="tonal">
              <v-icon color="white" size="28">mdi-package-variant-closed</v-icon>
            </v-avatar>
            <div>
              <h1 class="text-h4 font-weight-bold text-grey-darken-4 mb-1">Estoque por Depósito</h1>
              <div class="text-caption text-grey">
                Produtos com estoque detalhado por depósito
              </div>
            </div>
          </div>
          
          <v-spacer />
          
          <div class="d-flex align-center mt-2 mt-sm-0">
            <v-btn
              color="grey-darken-1"
              variant="text"
              @click="voltarMenu"
              class="text-none mr-2"
              prepend-icon="mdi-arrow-left"
            >
              Voltar
            </v-btn>
            
           
          </div>
        </div>
      </div>
    </div>

    <!-- ================= FILTROS EXPANSÍVEIS ================= -->
    <v-card class="filters-card mb-4" elevation="2">
      <v-card-title class="filters-header pa-4">
        <div class="d-flex align-center w-100">
          <!-- Seção Esquerda - Título e Info -->
          <div class="d-flex align-center cursor-pointer flex-grow-1" @click="toggleFilters">
            <v-icon class="mr-2" color="white">mdi-filter-variant</v-icon>
            <span class="filters-title">Filtros</span>
            
            <div class="d-flex align-center gap-2 ml-4">
              <v-chip variant="tonal" size="small">
                {{ pagination.total }} registros
              </v-chip>
              
              <v-chip 
                v-if="hasActiveFilters"
                color="warning" 
                variant="tonal" 
                size="small"
              >
                {{ activeFiltersCount }} filtro(s)
              </v-chip>
            </div>
          </div>
          
          <!-- Seção Central - Botões de Ação -->
          <div class="d-flex align-center gap-2 mr-2">
            <v-btn
              color="white"
              variant="flat"
              size="small"
              @click="fetchData"
              :loading="loading"
            >
              <v-icon size="18" color="grey-darken-2">mdi-refresh</v-icon>
              <span class="ml-2 text-grey-darken-2">Atualizar</span>
            </v-btn>
            
            <v-btn
              class="ml-5"
              color="white"
              variant="flat"
              size="small"
              @click="limparFiltros"
              :disabled="!hasActiveFilters"
            >
              <v-icon size="18" :color="hasActiveFilters ? 'grey-darken-2' : 'grey-lighten-1'">mdi-filter-off</v-icon>
              <span class="ml-2" :class="hasActiveFilters ? 'text-grey-darken-2' : 'text-grey-lighten-1'">Limpar</span>
            </v-btn>
          </div>
          
          <!-- Botão Expandir/Recolher -->
          <v-btn
            icon
            variant="text"
            size="small"
            color="white"
            @click="toggleFilters"
          >
            <v-icon size="20" :class="{ 'rotate-180': filtersExpanded }">
              mdi-chevron-down
            </v-icon>
            <v-tooltip activator="parent">
              {{ filtersExpanded ? 'Recolher Filtros' : 'Expandir Filtros' }}
            </v-tooltip>
          </v-btn>
        </div>
      </v-card-title>
      
      <!-- Conteúdo Expansível -->
      <v-expand-transition>
        <div v-show="filtersExpanded">
          <v-divider />
          <v-card-text class="pa-4">
            <v-row dense>
              <v-col cols="12" md="3">
                <v-text-field
                  v-model="filtros.codigo_produto"
                  label="Código do Produto"
                  placeholder="Ex: 001..."
                  prepend-inner-icon="mdi-barcode"
                  variant="outlined"
                  clearable
                  hide-details
                  density="compact"
                  @keyup.enter="fetchData"
                />
              </v-col>

              <v-col cols="12" md="3">
                <v-text-field
                  v-model="filtros.nome_produto"
                  label="Nome do Produto"
                  placeholder="Buscar por nome..."
                  prepend-inner-icon="mdi-magnify"
                  variant="outlined"
                  clearable
                  hide-details
                  density="compact"
                  @keyup.enter="fetchData"
                />
              </v-col>

              <v-col cols="12" md="2">
                <v-select
                  v-model="filtros.codigo_grupo_produto"
                  :items="gruposProdutos"
                  item-title="nome_grupo_produto"
                  item-value="codigo_grupo"
                  label="Grupo"
                  placeholder="Todos"
                  prepend-inner-icon="mdi-shape-outline"
                  variant="outlined"
                  clearable
                  hide-details
                  density="compact"
                />
              </v-col>

              <v-col cols="12" md="2">
                <v-select
                  v-model="filtros.codigo_deposito"
                  :items="depositos"
                  item-title="nome_deposito"
                  item-value="id_deposito"
                  label="Depósito"
                  placeholder="Todos"
                  prepend-inner-icon="mdi-warehouse"
                  variant="outlined"
                  clearable
                  hide-details
                  density="compact"
                />
              </v-col>

              <v-col cols="12" md="2">
                <v-text-field
                  v-model.number="filtros.estoque_fisico_min"
                  label="Est. Físico ≥"
                  placeholder="Mín."
                  prepend-inner-icon="mdi-package-variant"
                  variant="outlined"
                  clearable
                  hide-details
                  density="compact"
                  type="number"
                  @keyup.enter="fetchData"
                />
              </v-col>
            </v-row>

            <v-row dense class="mt-2">
              <v-col cols="12" md="2">
                <v-text-field
                  v-model.number="filtros.comprometido_min"
                  label="Comprom. ≥"
                  placeholder="Mín."
                  prepend-inner-icon="mdi-lock"
                  variant="outlined"
                  clearable
                  hide-details
                  density="compact"
                  type="number"
                  @keyup.enter="fetchData"
                />
              </v-col>

              <v-col cols="12" md="2">
                <v-text-field
                  v-model.number="filtros.disponivel_min"
                  label="Disponível ≥"
                  placeholder="Mín."
                  prepend-inner-icon="mdi-check-circle"
                  variant="outlined"
                  clearable
                  hide-details
                  density="compact"
                  type="number"
                  @keyup.enter="fetchData"
                />
              </v-col>

              <v-col cols="12" md="8" class="d-flex justify-end align-center">
                <v-btn
                  color="blue-darken-1"
                  variant="flat"
                  @click="fetchData"
                  :loading="loading"
                >
                  <v-icon start>mdi-filter</v-icon>
                  Filtrar
                </v-btn>
              </v-col>
            </v-row>
          </v-card-text>
        </div>
      </v-expand-transition>
    </v-card>

    <!-- ================= GRID / TABELA ================= -->
    <v-card class="table-card" elevation="2">
      <v-data-table-server
        v-model:items-per-page="itemsPerPage"
        :headers="headers"
        :items="produtos"
        :items-length="pagination.total"
        :loading="loading"
        :page="pagination.current_page"
        :sort-by="sortBy"
        item-key="codigo_produto"
        class="scrollable-table"
        density="compact"
        fixed-header
        height="calc(100vh - 400px)"
        @update:options="onOptionsUpdate"
      >
        <!-- Código -->
        <template #item.codigo_produto="{ item }">
          <v-chip
            color="blue-darken-1"
            variant="tonal"
            size="small"
          >
            {{ item.codigo_produto }}
          </v-chip>
        </template>

        <!-- Nome -->
        <template #item.nome_produto="{ item }">
          <v-chip
            color="teal"
            variant="tonal"
            size="small"
          >
            {{ item.nome_produto }}
          </v-chip>
        </template>

        <!-- Grupo -->
        <template #item.nome_grupo_produto="{ item }">
          <v-chip
            color="purple"
            variant="tonal"
            size="x-small"
          >
            {{ item.nome_grupo_produto }}
          </v-chip>
        </template>

        <!-- Depósito -->
        <template #item.nome_deposito="{ item }">
          <v-chip
            color="blue"
            variant="tonal"
            size="x-small"
          >
            {{ item.nome_deposito }}
          </v-chip>
        </template>

        <!-- Estoque Físico -->
        <template #item.estoque_fisico="{ item }">
          <span :class="item.estoque_fisico > 0 ? 'text-success' : 'text-grey'">
            {{ formatNumber(item.estoque_fisico) }}
          </span>
        </template>

        <!-- Estoque Comprometido -->
        <template #item.estoque_comprometido="{ item }">
          <span :class="item.estoque_comprometido > 0 ? 'text-warning' : 'text-grey'">
            {{ formatNumber(item.estoque_comprometido) }}
          </span>
        </template>

        <!-- Estoque Disponível -->
        <template #item.estoque_disponivel="{ item }">
          <span :class="item.estoque_disponivel > 0 ? 'text-success font-weight-bold' : 'text-error'">
            {{ formatNumber(item.estoque_disponivel) }}
          </span>
        </template>

        <!-- Nenhum resultado -->
        <template #no-data>
          <div class="text-center pa-6 text-grey">
            Nenhum produto encontrado. Ajuste os filtros e clique em "Filtrar".
          </div>
        </template>
      </v-data-table-server>
    </v-card>

  </div>
</template>

<script>
import { sapeJodarcService } from "@/services/sapeJodarcService";
import { useToast } from "vue-toastification";

const toast = useToast();

export default {
  name: "ConsultaProdutosEstoques",

  data() {
    return {
      produtos: [],
      gruposProdutos: [],
      depositos: [],
      loading: false,
      initialized: false,
      itemsPerPage: 50,
      filtersExpanded: false,
      sortBy: [],

      filtros: {
        codigo_produto: "",
        nome_produto: "",
        codigo_grupo_produto: null,
        codigo_deposito: null,
        estoque_fisico_min: null,
        comprometido_min: null,
        disponivel_min: null,
      },

      pagination: {
        total: 0,
        per_page: 50,
        current_page: 1,
        last_page: 1,
        from: 0,
        to: 0,
      },

      headers: [
        { title: "Código", key: "codigo_produto", width: "120px" },
        { title: "Produto", key: "nome_produto" },
        { title: "Grupo", key: "nome_grupo_produto", width: "150px" },
        { title: "Depósito", key: "nome_deposito", width: "150px" },
        { title: "Est. Físico", key: "estoque_fisico", width: "100px", align: "end" },
        { title: "Comprometido", key: "estoque_comprometido", width: "110px", align: "end" },
        { title: "Disponível", key: "estoque_disponivel", width: "100px", align: "end" },
      ],
    };
  },

  computed: {
    hasActiveFilters() {
      return (
        this.filtros.codigo_produto ||
        this.filtros.nome_produto ||
        this.filtros.codigo_grupo_produto ||
        this.filtros.codigo_deposito ||
        this.filtros.estoque_fisico_min !== null ||
        this.filtros.comprometido_min !== null ||
        this.filtros.disponivel_min !== null
      );
    },
    activeFiltersCount() {
      let count = 0;
      if (this.filtros.codigo_produto) count++;
      if (this.filtros.nome_produto) count++;
      if (this.filtros.codigo_grupo_produto) count++;
      if (this.filtros.codigo_deposito) count++;
      if (this.filtros.estoque_fisico_min !== null) count++;
      if (this.filtros.comprometido_min !== null) count++;
      if (this.filtros.disponivel_min !== null) count++;
      return count;
    },
  },

  methods: {
    toggleFilters() {
      this.filtersExpanded = !this.filtersExpanded;
    },

    async fetchData() {
      this.loading = true;
      try {
        const params = {
          page: this.pagination.current_page,
          per_page: this.pagination.per_page,
          ...this.filtros,
          sort_by: this.sortBy.length > 0 ? this.sortBy[0].key : undefined,
          sort_order: this.sortBy.length > 0 ? this.sortBy[0].order : undefined,
        };

        const response = await sapeJodarcService.sapeProdutosEstoquesDepositos(params);

        if (response?.success) {
          this.produtos = Array.isArray(response.data) ? response.data : [];
          if (response.pagination) {
            this.pagination = response.pagination;
            this.inputPage = response.pagination.current_page;
          }
        } else {
          this.produtos = [];
          toast.error(response?.message || "Erro ao carregar produtos");
        }
      } catch (error) {
        console.error(error);
        toast.error("Erro ao carregar produtos");
        this.produtos = [];
      } finally {
        this.loading = false;
      }
    },

    async fetchGrupos() {
      try {
        const response = await sapeJodarcService.sapeProdutosGrupo({});
        if (response?.success) {
          this.gruposProdutos = Array.isArray(response.data) ? response.data : [];
        }
      } catch (error) {
        console.error("Erro ao carregar grupos:", error);
      }
    },

    async fetchDepositos() {
      try {
        const response = await sapeJodarcService.sapeDeposito({});
        if (response?.success) {
          this.depositos = Array.isArray(response.data) ? response.data : [];
        }
      } catch (error) {
        console.error("Erro ao carregar depósitos:", error);
      }
    },

    onOptionsUpdate({ page, itemsPerPage, sortBy }) {
      if (this.loading) return;
      
      const currentSortBy = JSON.stringify(this.sortBy);
      const newSortBy = JSON.stringify(sortBy || []);
      
      if (!this.initialized) {
        this.initialized = true;
        this.pagination.current_page = page;
        this.pagination.per_page = itemsPerPage;
        this.itemsPerPage = itemsPerPage;
        this.sortBy = sortBy || [];
        this.fetchData();
        return;
      }
      
      const pageChanged = page !== this.pagination.current_page;
      const perPageChanged = itemsPerPage !== this.pagination.per_page;
      const sortChanged = currentSortBy !== newSortBy;
      
      if (!pageChanged && !perPageChanged && !sortChanged) return;
      
      this.pagination.current_page = page;
      this.itemsPerPage = itemsPerPage;
      this.pagination.per_page = itemsPerPage;
      this.sortBy = sortBy || [];
      this.fetchData();
    },

    limparFiltros() {
      this.filtros = {
        codigo_produto: "",
        nome_produto: "",
        codigo_grupo_produto: null,
        codigo_deposito: null,
        estoque_fisico_min: null,
        comprometido_min: null,
        disponivel_min: null,
      };
      this.pagination.current_page = 1;
      this.fetchData();
    },

    formatNumber(value) {
      if (value === null || value === undefined) return "0";
      return Number(value).toLocaleString("pt-BR", {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      });
    },

    voltarMenu() {
      this.$router.push({ name: "Menu" });
    },
  },

  mounted() {
    this.fetchGrupos();
    this.fetchDepositos();
  },
};
</script>

<style scoped>
/* ✅ CONTAINER PRINCIPAL */
.produtos-container {
  padding: 24px;
  background: #e4edfb;
  min-height: 100vh;
}

.page-header {
  margin-bottom: 16px;
}

/* ✅ ESTILOS DOS FILTROS */
.filters-card {
  border-radius: 12px;
  overflow: hidden;
}

.filters-header {
  background: linear-gradient(135deg, #1976d2, #1565c0);
  color: white;
}

.filters-title {
  font-size: 1rem;
  font-weight: 600;
}

/* ✅ ROTAÇÃO DO ÍCONE EXPANDIR */
.rotate-180 {
  transform: rotate(180deg);
  transition: transform 0.3s ease;
}

.cursor-pointer {
  cursor: pointer;
}

/* ✅ ESTILOS DA TABELA */
.table-card {
  border-radius: 12px;
  overflow: hidden;
}

/* ✅ SCROLL CUSTOMIZADO PARA TABELA */
.scrollable-table :deep(.v-table__wrapper) {
  overflow-x: auto !important;
  overflow-y: auto !important;
}

.scrollable-table :deep(.v-table__wrapper)::-webkit-scrollbar {
  width: 8px;
  height: 8px;
}

.scrollable-table :deep(.v-table__wrapper)::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 4px;
}

.scrollable-table :deep(.v-table__wrapper)::-webkit-scrollbar-thumb {
  background: linear-gradient(135deg, #1976d2, #2196f3);
  border-radius: 4px;
}

.scrollable-table :deep(.v-table__wrapper)::-webkit-scrollbar-thumb:hover {
  background: linear-gradient(135deg, #1565c0, #1976d2);
}

/* ✅ HEADERS DA TABELA */
.scrollable-table :deep(.v-data-table__th) {
  font-weight: 600 !important;
  background-color: #f8f9fa !important;
  position: sticky !important;
  top: 0 !important;
  z-index: 10 !important;
}

/* ✅ COLUNA CÓDIGO FIXA */
.scrollable-table :deep(.v-data-table__th:first-child),
.scrollable-table :deep(.v-data-table__td:first-child) {
  position: sticky !important;
  left: 0 !important;
  z-index: 5 !important;
  background: white !important;
  box-shadow: 2px 0 4px rgba(0, 0, 0, 0.1) !important;
}

.scrollable-table :deep(.v-data-table__th:first-child) {
  z-index: 15 !important;
  background: #f8f9fa !important;
}

/* ✅ HOVER DA LINHA */
.scrollable-table :deep(.v-data-table__tr:hover) {
  background-color: rgba(25, 118, 210, 0.05) !important;
}

.scrollable-table :deep(.v-data-table__tr:hover .v-data-table__td:first-child) {
  background-color: rgba(25, 118, 210, 0.05) !important;
}

/* ✅ TRUNCATE */
.text-truncate {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* ✅ RESPONSIVIDADE */
@media (max-width: 768px) {
  .produtos-container {
    padding: 16px;
  }
  
  .scrollable-table :deep(.v-data-table__th),
  .scrollable-table :deep(.v-data-table__td) {
    font-size: 0.875rem;
  }
}
</style>
